import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { createPortal } from 'react-dom';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';
import { useDispatch, useSelector } from 'react-redux';
import LhRichTextEditor from '../../../../components/LhRichTextEditor';
import { AppDispatch, RootState } from '../../../../store';
import {
  ChangeLogEntry,
  createHomepageChangeLogFromRedux,
  updateHomepageChangeLogFromRedux,
  deleteHomepageChangeLog,
  setChangeLogDraft,
  resetChangeLogDraft,
} from '../../../../store/slices/adminSlice';
import LHDataTable from '../../../../components/LHDataTable';
import { AdminColors } from '../../constants/adminColors';
import { useToast } from '../../../../components/LhToastContainer';
import AdminButton from '../../components/AdminButton';
import AdminModal from '../../components/AdminModal';
import { AdminFormField } from '../../components/AdminFormField';
import LhDatePicker from '../../../../components/LhDatePicker';
import RowActions from './RowActions';
import DeleteConfirmModal from './DeleteConfirmModal';
import EmptyState from './EmptyState';
import { styles } from './styles';
import { formatDate as formatDateHelper } from './helpers';
import { formatUserName } from '../../../../utils/emailTouserName';
import { formatDate } from '../../../../utils/dateFormatter';

export interface ChangeLogTabProps {
  showAdd: boolean;
  onCloseAdd: () => void;
}

const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const stripHtmlToPlainText = (value: string): string => {
  if (!value) return '';
  const withoutTags = value
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&#58;/gi, ':');

  return withoutTags.replace(/\s+/g, ' ').trim();
};

const getDetailsTextLength = (value: string): number => stripHtmlToPlainText(value).length;

// Portal tooltip for Change Details column hover
const DetailsTooltip: React.FC<{
  visible: boolean;
  position: { x: number; y: number } | null;
  text: string;
}> = ({ visible, position, text }) => {
  if (Platform.OS !== 'web' || !visible || !position || !text) return null;
  const win: any = typeof globalThis !== 'undefined' ? (globalThis as any) : {};
  if (!win.document) return null;

  const overlay = (
    <View
      style={{
        position: 'fixed' as any,
        left: position.x + 14,
        top: position.y - 10,
        maxWidth: 360,
        zIndex: 2147483647,
        pointerEvents: 'none' as any,
      }}
    >
      <View
        style={{
          paddingHorizontal: 12,
          paddingVertical: 8,
          borderRadius: 6,
          backgroundColor: '#1C1C1C',
        }}
      >
        <Text style={{ color: '#FFFFFF', fontSize: 12, fontWeight: '400', lineHeight: 18 }}>
          {text}
        </Text>
      </View>
    </View>
  );

  return createPortal(overlay, win.document.body);
};

const ChangeLogTab: React.FC<ChangeLogTabProps> = ({ showAdd, onCloseAdd }) => {
  const { colors, resolvedTheme } = useTheme();
  const isAnyDark = resolvedTheme === 'dark';
  const datePickerColorScheme = resolvedTheme === 'dark' ? 'dark' : 'light';

  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const { changeLogs, changeLogsLoading: isLoading, changeLogsError: error, changeLogDraft } = useSelector((state: RootState) => state.configuration.homepageConfig);
  const [editItem, setEditItem] = React.useState<ChangeLogEntry | null>(null);
  const [details, setDetails] = useState('');
  const [goLiveDate, setGoLiveDate] = useState<Date | null>(null);
  const [detailsError, setDetailsError] = useState('');
  const [goLiveDateError, setGoLiveDateError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ChangeLogEntry | null>(null);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hoveredDetails, setHoveredDetails] = React.useState<{ text: string; x: number; y: number } | null>(null);

  const detailsTextLength = React.useMemo(() => getDetailsTextLength(details), [details]);

  React.useEffect(() => {
    if (editItem) {
      setDetails(editItem.details);
      setGoLiveDate(editItem.goLiveDate ? parseLocalDate(editItem.goLiveDate) : null);
    } else if (showAdd) {
      setDetails(changeLogDraft.details || '');
      setGoLiveDate(changeLogDraft.goLiveDate ? parseLocalDate(changeLogDraft.goLiveDate) : null);
    } else {
      setDetails('');
      setGoLiveDate(null);
    }
  }, [editItem, showAdd, changeLogDraft.details, changeLogDraft.goLiveDate]);

  const isDirty = editItem
    ? (details !== editItem.details || formatDateHelper(goLiveDate) !== editItem.goLiveDate)
    : (!!details || !!goLiveDate);

  const doClose = () => {
    onCloseAdd();
    setEditItem(null);
    setDetails('');
    setGoLiveDate(null);
    setDetailsError('');
    setGoLiveDateError('');
    setShowUnsavedWarning(false);
    dispatch(resetChangeLogDraft());
  };

  const handleClose = () => {
    if (isDirty) { setShowUnsavedWarning(true); return; }
    doClose();
  };
  
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    try {
      setIsDeleting(true);
      const resultAction = await dispatch(deleteHomepageChangeLog({ id: target.id }));
      if (deleteHomepageChangeLog.fulfilled.match(resultAction)) {
        toast.showToast({ header: 'Success', subtext: 'Change log entry deleted successfully.', variant: 'success' });
        setDeleteTarget(null);
      } else {
        toast.showToast({
          header: 'Error',
          subtext: resultAction.payload || 'Failed to delete change log entry.',
          variant: 'error',
        });
      }
    } catch (error: any) {
      toast.showToast({
        header: 'Error',
        subtext: error || 'Failed to delete change log entry.',
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = async () => {
    let valid = true;
    const normalizedDetails = stripHtmlToPlainText(details);
    if (!normalizedDetails) {
      setDetailsError('Change Details are required.');
      valid = false;
    } else if (normalizedDetails.length > 500) {
      setDetailsError('Change Details must be 500 characters or fewer.');
      valid = false;
    } else {
      setDetailsError('');
    }
    if (!goLiveDate) {
      setGoLiveDateError('Go-Live Date is required.');
      valid = false;
    } else {
      setGoLiveDateError('');
    }
    if (!valid) return;

    const goLive = formatDateHelper(goLiveDate);
    setIsSaving(true);
    if (editItem) {
      dispatch(setChangeLogDraft({
        details,
        goLiveDate: goLive,
        displayOrder: changeLogs.findIndex((item) => item.id === editItem.id) + 1,
        locType: 'WH',
      }));

      const resultAction = await dispatch(updateHomepageChangeLogFromRedux({ id: editItem.id }));
      if (updateHomepageChangeLogFromRedux.fulfilled.match(resultAction)) {
        toast.showToast({ header: 'Success', subtext: 'Change log entry updated successfully.', variant: 'success' });
        doClose();
      } else {
        toast.showToast({
          header: 'Error',
          subtext: resultAction.payload || 'Failed to update change log entry.',
          variant: 'error',
        });
      }
    } else {
      dispatch(setChangeLogDraft({
        details,
        goLiveDate: goLive,
        displayOrder: changeLogs.length + 1,
        locType: 'WH',
      }));

      const resultAction = await dispatch(createHomepageChangeLogFromRedux());
      if (createHomepageChangeLogFromRedux.fulfilled.match(resultAction)) {
        toast.showToast({ header: 'Success', subtext: 'Change log entry created successfully.', variant: 'success' });
        doClose();
      } else {
        toast.showToast({
          header: 'Error',
          subtext: resultAction.payload || 'Failed to create change log entry.',
          variant: 'error',
        });
      }
    }
    setIsSaving(false);
  };

  const modalVisible = showAdd || editItem !== null;

  const sortedChangeLog = [...changeLogs].sort((a, b) => {
    if (a.goLiveDate > b.goLiveDate) return -1;
    if (a.goLiveDate < b.goLiveDate) return 1;
    return 0;
  });

  const changeLogColumns = [
    {
      columnKey: 'details',
      header: 'Change Details',
      flexGrow: 2,
      // formatter feeds LHDataTable's width estimator a short string so minWidth
      // stays small and the table never forces horizontal scrolling.
      formatter: (value: string) => stripHtmlToPlainText(value).slice(0, 40),
      renderCell: (item: ChangeLogEntry) => {
        const fullText = stripHtmlToPlainText(item.details);
        // Hard-truncate to 80 chars — reliable on every platform/browser regardless
        // of flex/CSS behaviour. Tooltip shows the full text on hover.
        const displayText = fullText.length > 80 ? `${fullText.slice(0, 80)}\u2026` : fullText;
        const webHandlers: any = Platform.OS === 'web'
          ? {
              onMouseEnter: (e: any) => {
                const x = e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
                const y = e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
                setHoveredDetails({ text: fullText, x, y });
              },
              onMouseMove: (e: any) => {
                const x = e?.clientX ?? e?.nativeEvent?.clientX ?? 0;
                const y = e?.clientY ?? e?.nativeEvent?.clientY ?? 0;
                setHoveredDetails((prev) => (prev ? { ...prev, x, y } : null));
              },
              onMouseLeave: () => setHoveredDetails(null),
            }
          : {};
        return (
          <View style={{ width: '100%' }} {...webHandlers}>
            <Text style={{ fontSize: 12, color: isAnyDark ? colors.text.primary : AdminColors.textPrimary }}>
              {displayText}
            </Text>
          </View>
        );
      },
    },
    {
      columnKey: 'goLiveDate',
      header: 'Go-Live Date',
      flexGrow: 1,
    },
    {
      columnKey: 'createdBy',
      header: 'Created By',
      flexGrow: 1,
      // formatter caps width estimator so email addresses don't inflate minWidth
      formatter: (value: string) => (value ?? '').slice(0, 20),
      renderCell: (item: ChangeLogEntry) => (
        <Text style={{ fontSize: 12, color: isAnyDark ? colors.text.secondary : AdminColors.textPrimary }}>
          {formatUserName(item.createdBy)}
        </Text>
      ),
    },
    {
      columnKey: 'createdDate',
      header: 'Created Time',
      flexGrow: 1,
      renderCell: (item: ChangeLogEntry) => (
        <Text style={{ fontSize: 12, color: isAnyDark ? colors.text.secondary : AdminColors.textPrimary }}>
          {formatDate(item.createdDate)}
        </Text>
      ),
    },
    {
      columnKey: '_actions',
      header: 'Actions',
      flexGrow: 0.5,
      sortable: false,
      renderCell: (item: ChangeLogEntry) => (
        <RowActions onEdit={() => setEditItem(item)} onDelete={() => setDeleteTarget(item)} />
      ),
    },
  ];

  return (
    <>
      <DetailsTooltip
        visible={hoveredDetails !== null}
        position={hoveredDetails}
        text={hoveredDetails?.text ?? ''}
      />
      <View>
      {isLoading ? (
        <EmptyState title="Loading Change Log" subtitle="Please wait..." />
      ) : error ? (
        <EmptyState title="Unable to Load Change Log" subtitle={error} />
      ) : changeLogs.length === 0 ? (
        <EmptyState />
      ) : (
        <LHDataTable
          data={sortedChangeLog}
          columns={changeLogColumns}
          tableHeader=""
          tableDescription={`Showing ${sortedChangeLog.length} result(s)`}
          onRowClick={() => { /* Disable row click overlay */ }}
          disableRowHighlight
        />
      )}

      <AdminModal
        visible={modalVisible}
        title={editItem ? 'Edit Log Entry' : 'Add New Log Entry'}
        onClose={handleClose}
        footer={
          <>
            <AdminButton label="Cancel" variant="secondary" onPress={doClose} disabled={isSaving} />
            <AdminButton label={editItem ? 'Save Changes' : 'Save'} variant="primary" onPress={handleSave} loading={isSaving} />
          </>
        }
      >
        {showUnsavedWarning && (
          <View
            style={[
              clLocalStyles.warningBanner,
              isAnyDark && {
                backgroundColor: colors.statusBackground.warning,
                borderColor: colors.status.warning,
              },
            ]}
          >
            <View style={clLocalStyles.warningBannerContent}>
              <Text style={[clLocalStyles.warningIcon, isAnyDark && { color: colors.status.warning }]}>⚠</Text>
              <Text style={[clLocalStyles.warningMessage, isAnyDark && { color: colors.status.warning }]}>
                You have unsaved changes. To discard and close, click{' '}
                <Text style={clLocalStyles.warningMessageBold}>Cancel</Text>.
              </Text>
            </View>
            <TouchableOpacity onPress={() => setShowUnsavedWarning(false)} style={clLocalStyles.warningCloseBtn}>
              <Text style={[clLocalStyles.warningCloseText, isAnyDark && { color: colors.status.warning }]}>✕</Text>
            </TouchableOpacity>
          </View>
        )}
        <AdminFormField label="Change Details" required error={detailsError}>
          <LhRichTextEditor
            value={details}
            onChange={(html) => {
              setDetails(html);
              if (stripHtmlToPlainText(html)) setDetailsError('');
            }}
            placeholder="Describe the application change, new feature, or update..."
            hasError={!!detailsError}
            minHeight={140}
            isDark={isAnyDark}
          />
          <Text style={[styles.charCount, isAnyDark && { color: colors.text.disabled }]}>{detailsTextLength} / 500 — Supports bullet points and line breaks</Text>
        </AdminFormField>
        <AdminFormField
          label="Go-Live Date"
          required
          hint={goLiveDateError ? undefined : 'Date the change was deployed to production'}
          error={goLiveDateError}
        >
          <LhDatePicker
            value={goLiveDate}
            onChange={(date) => { setGoLiveDate(date); if (date) setGoLiveDateError(''); }}
            placeholder="Select Go-Live Date"
            colorScheme={isAnyDark ? 'dark' : 'light'}
            iconColor={isAnyDark ? colors.text.primary : '#555'}
            disabledIconColor={isAnyDark ? colors.text.disabled : '#bdbdbd'}
          />
        </AdminFormField>
      </AdminModal>

      <DeleteConfirmModal
        visible={deleteTarget !== null}
        itemLabel={stripHtmlToPlainText(deleteTarget?.details ?? '')}
        message="Are you sure you want to delete this change log entry?"
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
      />
    </View>
    </>
  );
};

export default ChangeLogTab;

const clLocalStyles = StyleSheet.create({
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    backgroundColor: '#FFF3E0',
    borderWidth: 1,
    borderColor: '#FFB74D',
    borderRadius: 6,
    marginBottom: 12,
    padding: 12,
  },
  warningBannerContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  warningIcon: {
    fontSize: 15,
    color: '#E65100',
  },
  warningMessage: {
    flex: 1,
    fontSize: 13,
    color: '#E65100',
    lineHeight: 18,
    fontFamily: FontFamily.regular,
  },
  warningMessageBold: {
    fontWeight: '700',
    fontFamily: FontFamily.medium,
  },
  warningCloseBtn: {
    padding: 2,
    marginLeft: 8,
  },
  warningCloseText: {
    fontSize: 14,
    color: '#E65100',
    fontWeight: '700',
  },
});
